// Zod runtime schemas for the Retirement Planner /v1 wire contract.
// Source of truth: shared/openapi/retirement-planner.v1.yaml
//
// The per-DTO base schemas mirror `openapi-zod-client` output (field-level
// constraints from the OpenAPI). The CI parity job regenerates the base and
// diffs it — drift fails the build (SD7/BR4.1).
//
// OpenAPI cannot express the BR1.2 single-currency-per-record rule (cross-field),
// so the generators emit field-level validators ONLY. The currency-match rule is
// HAND-WRITTEN below as a `.superRefine()` (SD4) layered over the generated base
// and kept in a clearly marked block so regeneration preserves it.
//
// Money is integer minor units + a currency code — never a float (BR1.1).

import { z } from "zod";

export const PROBLEM_CODES = [
  "CURRENCY_MISMATCH",
  "RUNS_OUT_OF_RANGE",
  "INVALID_PROJECTION_INPUT",
  "VALIDATION_ERROR",
  "UNAUTHORIZED",
  "NOT_FOUND",
  "SERVER_ERROR",
] as const;

export const currencySchema = z.enum(["THB", "USD"]);
export const accountTypeSchema = z.enum([
  "thai_fund",
  "thai_stock_set",
  "us_stock_etf",
  "cash",
  "other",
]);
export const onTrackSchema = z.enum(["on_track", "caution", "behind"]);
export const stressPresetSchema = z.enum([
  "none",
  "market_crash",
  "high_inflation",
  "combined",
]);
export const problemCodeSchema = z.enum(PROBLEM_CODES);

/** Money: integer minor units + currency. `.int()` enforces BR1.1 (never a float). */
export const moneySchema = z
  .object({
    amountMinorUnits: z.number().int(),
    currency: currencySchema,
  })
  .strict();

export const yearPointSchema = z.object({
  age: z.number().int(),
  nominal: moneySchema,
  real: moneySchema,
});

// --- generated base (field-level only) ---
const assumptionsBase = z.object({
  currentAge: z.number().int().min(0).max(120),
  targetRetirementAge: z.number().int().min(1).max(120),
  currentSavings: moneySchema,
  monthlyContribution: moneySchema,
  targetAnnualSpending: moneySchema,
  expectedNominalReturnPct: z.number(),
  inflationRatePct: z.number(),
  safeWithdrawalRatePct: z.number(),
  // Optional with a default (matches YAML `default: 15.0` and Python `float | None = 15.0`).
  // A field without .optional()/.default() would be REQUIRED in Zod — a PY<->Zod parity break.
  volatilityPct: z.number().nullable().default(15.0),
  planToAge: z.number().int().min(1).max(120),
  currency: currencySchema,
});

// >>> AIDLC-HANDWRITTEN (SD4/BR1.2) — excluded from the CI parity diff; NOT generator output.
// Every embedded Money.currency MUST equal the record currency; else CURRENCY_MISMATCH.
const withCurrencyMatch = <T extends z.ZodTypeAny>(base: T) =>
  base.superRefine((val: z.infer<typeof assumptionsBase>, ctx) => {
    for (const key of [
      "currentSavings",
      "monthlyContribution",
      "targetAnnualSpending",
    ] as const) {
      if (val[key].currency !== val.currency) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "every Money.currency must equal the record currency",
          path: [key, "currency"],
          params: { code: "CURRENCY_MISMATCH" },
        });
      }
    }
  });

export const assumptionsSchema = withCurrencyMatch(assumptionsBase);
export const assumptionsInputSchema = withCurrencyMatch(assumptionsBase);
// <<< AIDLC-HANDWRITTEN

export const accountSchema = z.object({
  accountId: z.string().uuid(),
  name: z.string().min(1),
  type: accountTypeSchema,
  value: moneySchema,
});

export const accountInputSchema = z.object({
  name: z.string().min(1),
  type: accountTypeSchema,
  value: moneySchema,
});

export const accountsResponseSchema = z.object({
  accounts: z.array(accountSchema),
  netWorth: moneySchema,
});

export const projectionResponseSchema = z.object({
  series: z.array(yearPointSchema),
  onTrack: onTrackSchema,
  realBalanceAtTarget: moneySchema,
});

export const retirementRequestSchema = z.object({
  extraMonthlyContribution: moneySchema.optional(),
  spendingAdjustmentPct: z.number().optional(),
  retireAgeDeltaYears: z.number().int().optional(),
});

export const retirementResponseSchema = z.object({
  earliestRetirementAge: z.number().int().nullable(),
  real: z.boolean(),
});

export const monteCarloRequestSchema = z.object({
  runs: z.number().int().min(1000).max(50000).default(10000),
  stressPreset: stressPresetSchema.default("none"),
});

export const fanChartSchema = z.object({
  p10: z.array(yearPointSchema),
  p50: z.array(yearPointSchema),
  p90: z.array(yearPointSchema),
});

export const monteCarloResponseSchema = z.object({
  probabilityOfSuccess: z.number().min(0).max(1),
  runs: z.number().int(),
  seedReproducible: z.boolean().optional(),
  fan: fanChartSchema,
});

export const dashboardResponseSchema = z.object({
  netWorth: moneySchema,
  accounts: z.array(accountSchema),
  onTrack: onTrackSchema,
  projectedRetirementAge: z.number().int().nullable().optional(),
  probabilityOfSuccess: z.number().min(0).max(1),
  projectionSummary: z.array(yearPointSchema).optional(),
});

export const problemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  code: problemCodeSchema,
});
